/**
 * ONE status vocabulary for every Shoku product surface.
 *
 * Before this file the same concept wore a different colour on every screen:
 * the contractor site kept seven separate status->tone maps (job, booking,
 * quote, payout, appointment, bank, verification), the user site kept two more,
 * and between them they painted eight literal hex colours — four of which
 * existed in no token file at all. Four of those hues were warm oranges that
 * are indistinguishable at 12px, so colour carried almost no information.
 *
 * There are exactly SIX tones, in six different hue families:
 *
 *   pending   gold      waiting on someone else, nothing is wrong
 *   progress  orange    happening right now — the only "active" tone
 *   success   green     done, paid, confirmed
 *   info      blue-grey in flight at a third party, outcome unknown
 *   neutral   grey      over without succeeding: cancelled, declined, expired
 *   danger    red       failed and needs a human
 *
 * Each maps to a `--status-*` alias in app.css, which maps to a styles.css
 * token. Nothing here knows a colour.
 *
 * The labels live here too, in ONE case convention (sentence case), because
 * the same status used to read "Awaiting Sign-off" in a pill and "Awaiting
 * sign-off" in the section heading two lines above it.
 */

export type Tone = "pending" | "progress" | "success" | "info" | "neutral" | "danger";

export type StatusKind =
  | "job"
  | "booking"
  | "quote"
  | "payout"
  | "referral"
  | "appointment"
  | "bank"
  | "verification"
  | "payment";

interface Entry {
  tone: Tone;
  label: string;
}

/**
 * Keys are written in the canonical camelCase the Go API emits, but lookup is
 * squashed (see `squash`) so "PendingUserSignOff", "pending_user_sign_off" and
 * "pendingUserSignOff" all resolve. The wire has carried all three.
 */
const TABLE: Record<StatusKind, Record<string, Entry>> = {
  job: {
    pending: { tone: "pending", label: "Pending" },
    quoted: { tone: "progress", label: "Quoted" },
    accepted: { tone: "success", label: "Accepted" },
    scheduled: { tone: "pending", label: "Scheduled" },
    inProgress: { tone: "progress", label: "In progress" },
    // Genuinely unknown at this point — not a success and not a failure.
    paymentProviderPendingResult: { tone: "info", label: "Payment processing" },
    userPaid: { tone: "success", label: "Paid" },
    pendingUserSignOff: { tone: "pending", label: "Awaiting sign-off" },
    completed: { tone: "success", label: "Completed" },
    completedAndPaid: { tone: "success", label: "Completed & paid" },
    cancelled: { tone: "neutral", label: "Cancelled" },
  },
  booking: {
    requested: { tone: "pending", label: "Requested" },
    accepted: { tone: "success", label: "Accepted" },
    declined: { tone: "neutral", label: "Declined" },
    expired: { tone: "neutral", label: "Expired" },
    paymentPending: { tone: "info", label: "Awaiting payment" },
    paymentFailed: { tone: "danger", label: "Payment failed" },
    paid: { tone: "success", label: "Paid" },
    inProgress: { tone: "progress", label: "In progress" },
    completed: { tone: "success", label: "Completed" },
    cancelledByUser: { tone: "neutral", label: "Cancelled by customer" },
    cancelledByContractor: { tone: "neutral", label: "Cancelled by you" },
    refunded: { tone: "info", label: "Refunded" },
    noShow: { tone: "danger", label: "No show" },
  },
  quote: {
    pending: { tone: "pending", label: "Pending" },
    accepted: { tone: "success", label: "Accepted" },
    rejected: { tone: "neutral", label: "Rejected" },
    expired: { tone: "neutral", label: "Expired" },
  },
  payout: {
    // Payout is created only after capture, so it is waiting to be disbursed,
    // not waiting on sign-off. The label reflects what the contractor is waiting for.
    pending: { tone: "pending", label: "Awaiting payout" },
    processing: { tone: "progress", label: "Processing" },
    succeeded: { tone: "success", label: "Paid" },
    failed: { tone: "danger", label: "Failed" },
  },
  /**
   * A referral partner's share of a completed job.
   *
   * Deliberately NOT `payout`, though both are money on its way to a bank
   * account. A partner's share is allocated by the Paystack split at capture
   * and rides the subaccount's own settlement, so there is nothing to sign off
   * and nothing to claim. user-api tells the two apart on purpose — it refuses
   * to send the stored Payout.Status, which really does say "Pending" on referrer
   * rows, because it was copy-pasted from the contractor path. Both are waiting
   * for their respective settlement run ("Awaiting payout"), not awaiting sign-off.
   */
  referral: {
    settled: { tone: "success", label: "Settled" },
    // The only other outcome the split can have. Nothing has gone wrong — the
    // money simply has not reached a settlement run yet.
    pending: { tone: "pending", label: "Awaiting payout" },
  },
  appointment: {
    scheduled: { tone: "pending", label: "Awaiting confirmation" },
    confirmed: { tone: "success", label: "Confirmed" },
    inProgress: { tone: "progress", label: "In progress" },
    completed: { tone: "success", label: "Completed" },
    cancelled: { tone: "neutral", label: "Cancelled" },
    rescheduled: { tone: "pending", label: "Rescheduled" },
  },
  bank: {
    pending: { tone: "pending", label: "Verification pending" },
    verified: { tone: "success", label: "Verified" },
    failed: { tone: "danger", label: "Verification failed" },
  },
  verification: {
    pending: { tone: "pending", label: "Pending" },
    verified: { tone: "success", label: "Verified" },
    rejected: { tone: "neutral", label: "Rejected" },
  },
  payment: {
    pending: { tone: "info", label: "Pending" },
    processing: { tone: "info", label: "Processing" },
    // Reserve-then-capture: the money is ring-fenced on the customer's card and
    // waiting on the two sign-offs, which is the definition of `pending` — not
    // a success, and nothing has gone wrong.
    preauthorized: { tone: "pending", label: "Funds held" },
    succeeded: { tone: "success", label: "Paid" },
    captured: { tone: "success", label: "Paid" },
    failed: { tone: "danger", label: "Failed" },
    // The hold was let go without a charge: over, and nobody is out of pocket.
    released: { tone: "neutral", label: "Hold released" },
    // A hold that could not be turned into a charge DOES need a human.
    captureFailed: { tone: "danger", label: "Charge failed" },
    refunded: { tone: "info", label: "Refunded" },
  },
};

/** Strips case and every separator, so "user-paid" == "USER_PAID" == "userPaid". */
function squash(value: string): string {
  return value.toLowerCase().replace(/[-_\s]/g, "");
}

const INDEX: Record<StatusKind, Record<string, Entry>> = Object.fromEntries(
  Object.entries(TABLE).map(([kind, entries]) => [
    kind,
    Object.fromEntries(Object.entries(entries).map(([key, entry]) => [squash(key), entry])),
  ]),
) as Record<StatusKind, Record<string, Entry>>;

function lookup(kind: StatusKind, status: string | null | undefined): Entry | undefined {
  if (!status) return undefined;
  return INDEX[kind][squash(status)];
}

/**
 * Tone for a status. An unrecognised value is `neutral` rather than a guess:
 * a status nobody has taught this table about is, by definition, not progress
 * and not a failure, and painting it either would be a lie.
 */
export function toneFor(kind: StatusKind, status: string | null | undefined): Tone {
  return lookup(kind, status)?.tone ?? "neutral";
}

/**
 * Human label for a status, sentence case. Unknown values fall back to the raw
 * value with its first letter capitalised — visible enough to be reported, but
 * never blank, which is what a strict map would have produced.
 */
export function labelFor(kind: StatusKind, status: string | null | undefined): string {
  const entry = lookup(kind, status);
  if (entry) return entry.label;
  if (!status) return "Unknown";
  const spaced = status.replace(/[-_]/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/** Every status key a kind knows about — used by the gallery and by tests. */
export function statusesFor(kind: StatusKind): string[] {
  return Object.keys(TABLE[kind]);
}

export const TONES: readonly Tone[] = [
  "pending",
  "progress",
  "success",
  "info",
  "neutral",
  "danger",
];
